import React, { useState, useEffect } from 'react';
import { useToast } from '@/context/ToastContext';
import axios from 'axios';
import { Shield, UserX, UserCheck, Crown, RotateCcw, BadgeCheck, ExternalLink, Undo2 } from 'lucide-react';
import ConfirmDialog from '@/components/ConfirmDialog';
import { frenchCompanyNumber } from '@/utils/siret';
import { BACKEND_URL } from '@/config';
import { roleLabel } from '@/utils/statusLabels';

const API = BACKEND_URL + '/api';
axios.defaults.withCredentials = true;

function formatDate(d) {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('fr-FR');
}

const TONES = {
  gold: { background: 'var(--gpale)', color: 'var(--gold)' },
  red:  { background: '#fef2f2', color: 'var(--red)' },
  grey: { background: '#f3f4f6', color: '#6B7280' },
  teal: { background: 'var(--tbg)', color: 'var(--teal)' },
};
const chipStyle = (tone) => ({ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, whiteSpace: 'nowrap', ...TONES[tone] });
const actionStyle = { fontSize: 11, padding: '3px 8px', display: 'inline-flex', alignItems: 'center', gap: 4 };

/** État PRO d'un transporteur : forçage admin prioritaire, sinon statut de l'abonnement Stripe. */
export function proState(u) {
  if (!u.roles?.includes('ROLE_CARRIER')) return null;
  if (u.pro_admin_override === true) return { label: 'Offert', tone: 'gold' };
  if (u.pro_admin_override === false) return { label: "Retiré par l'admin", tone: 'grey' };
  const sub = u.subscription;
  const ending = sub?.cancel_at_period_end ? ' · résilié' : '';
  if (sub?.status === 'trialing') return { label: `Essai${ending}`, tone: 'gold' };
  if (sub?.status === 'active') return { label: `Actif${ending}`, tone: 'gold' };
  if (sub?.status === 'past_due') return { label: u.pro_subscribed ? 'Paiement refusé' : 'Suspendu (impayé)', tone: 'red' };
  if (sub?.status) return { label: 'Terminé', tone: 'grey' };
  return null;
}

/** Numéro d'entreprise saisi à l'inscription (siret) ou dans le profil (siren). */
export const companyNumber = (u) => (u.siren || u.siret || '').trim();

const annuaireUrl = (num) => {
  const digits = num.replace(/\D/g, '');
  return digits.length === 9 || digits.length === 14
    ? `https://annuaire-entreprises.data.gouv.fr/rechercher?terme=${digits}`
    : null;
};

export default function AdminUsers() {
  const toast = useToast();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [proFilter, setProFilter] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [suspendTarget, setSuspendTarget] = useState(null);
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (roleFilter) params.role = roleFilter;
      if (statusFilter) params.status = statusFilter;
      if (proFilter) params.pro = proFilter;
      if (companyFilter) params.company = companyFilter;
      const { data } = await axios.get(`${API}/admin/users`, { params });
      setUsers(data.data || []);
      setTotal(data.meta?.total || 0);
    } catch { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [page, roleFilter, statusFilter, proFilter, companyFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const doAction = async (id, action) => {
    try {
      await axios.patch(`${API}/users/${id}/${action}`);
      toast.success(action === 'verify' ? 'Utilisateur vérifié.' : action === 'suspend' ? 'Compte suspendu.' : 'Compte réactivé.');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    }
  };

  const name = (u) => `${u.first_name || ''} ${u.last_name || ''}`.trim();

  const askPro = (u, value) => {
    if (value === true) {
      setPending({ u, kind: 'pro', value, variant: 'financial', icon: 'check', confirmLabel: 'Offrir le PRO',
        title: `Offrir le PRO à ${name(u)} ?`,
        info: "Commission à 7 %, trajets jusqu'à 1 500 kg et badge PRO, sans prélèvement, jusqu'à ce que vous le retiriez.",
        done: 'PRO offert.' });
    } else if (value === false) {
      setPending({ u, kind: 'pro', value, variant: 'destructive', icon: 'ban', confirmLabel: 'Retirer le PRO',
        title: `Retirer le PRO de ${name(u)} ?`,
        warn: "Ses prochaines missions repassent au taux standard, même avec un abonnement Stripe actif. Les missions en cours gardent leur taux.",
        done: 'PRO retiré.' });
    } else {
      setPending({ u, kind: 'pro', value: null, variant: 'financial', icon: 'check', confirmLabel: 'Rendre la main à Stripe',
        title: `Rendre la main à Stripe pour ${name(u)} ?`,
        info: "Le statut PRO suivra de nouveau son abonnement Stripe (sans abonnement : taux standard).",
        done: 'Le PRO suit de nouveau l’abonnement Stripe.' });
    }
  };

  const askCompany = (u, value) => {
    const num = companyNumber(u);
    setPending({ u, kind: 'company', value,
      variant: value ? 'financial' : 'destructive', icon: value ? 'check' : 'ban',
      confirmLabel: value ? "Valider l'entreprise" : 'Retirer la validation',
      title: value ? `Valider l'entreprise de ${name(u)} ?` : `Retirer la validation de ${name(u)} ?`,
      subtitle: `${u.company_name || 'Société sans nom'} — ${num ? `SIRET ${num}` : 'SIRET non renseigné'}`,
      [value ? 'info' : 'warn']: value
        ? "La mention « Entreprise » s'affichera sur ses annonces et ses colis pourront aller jusqu'à 1 500 kg."
        : "La mention « Entreprise » disparaît et ses colis sont de nouveau limités à 250 kg.",
      done: value ? 'Entreprise validée.' : 'Validation retirée.' });
  };

  const askRevert = (u) => {
    const num = companyNumber(u);
    setRejectReason('');
    setPending({ u, kind: 'revert', variant: 'destructive', icon: 'ban', confirmLabel: 'Repasser en Particulier',
      title: `Repasser ${name(u)} en Particulier ?`,
      subtitle: `${u.company_name || 'Société sans nom'} — ${num ? `SIRET ${num}` : 'SIRET non renseigné'}`,
      warn: "Refus de l'entreprise : le compte redevient Particulier (colis limités à 250 kg). L'utilisateur reçoit le motif et pourra refaire une demande.",
      done: 'Compte repassé en Particulier.' });
  };

  const confirmPending = async () => {
    const p = pending;
    if (p.kind === 'revert' && rejectReason.trim().length < 3) { toast.error('Indiquez un motif (3 caractères minimum).'); return; }
    setBusy(true);
    try {
      if (p.kind === 'pro') await axios.patch(`${API}/admin/users/${p.u.id}/subscription`, { pro_subscribed: p.value });
      else if (p.kind === 'revert') await axios.post(`${API}/admin/users/${p.u.id}/company/reject`, { reason: rejectReason.trim() });
      else await axios.patch(`${API}/admin/users/${p.u.id}/company`, { verified: p.value });
      toast.success(p.done);
      setPending(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  const KycBadge = ({ status }) => {
    const map = { VERIFIED: { label: 'Vérifié', bg: 'var(--tbg)', color: 'var(--teal)' }, PENDING: { label: 'En attente', bg: 'var(--warm)', color: 'var(--gold)' }, IN_PROGRESS: { label: 'En cours', bg: '#ede9fe', color: '#5b21b6' }, REJECTED: { label: 'Rejeté', bg: '#fef2f2', color: 'var(--red)' } };
    const s = map[status] || map.PENDING;
    return <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: s.bg, color: s.color }}>{s.label}</span>;
  };

  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontFamily: 'var(--f-display)', fontSize: 22, fontWeight: 800, marginBottom: 4 }}>Gestion des utilisateurs</div>
        <div style={{ fontSize: 14, color: 'var(--ink2)' }}>{total} utilisateurs au total</div>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <select data-testid="filter-role" className="field-select" style={{ width: 'auto' }} value={roleFilter} onChange={e => { setRoleFilter(e.target.value); setPage(1); }}>
          <option value="">Tous les rôles</option>
          <option value="ROLE_SENDER">Expéditeurs</option>
          <option value="ROLE_CARRIER">Transporteurs</option>
          <option value="ROLE_ADMIN">Admins</option>
        </select>
        <select data-testid="filter-status" className="field-select" style={{ width: 'auto' }} value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="">Tous les statuts</option>
          <option value="active">Actifs</option>
          <option value="suspended">Suspendus</option>
        </select>
        <select data-testid="filter-pro" className="field-select" style={{ width: 'auto' }} value={proFilter} onChange={e => { setProFilter(e.target.value); setPage(1); }}>
          <option value="">Tous les abonnements</option>
          <option value="active">PRO actifs</option>
          <option value="offered">PRO offerts</option>
        </select>
        <select data-testid="filter-company" className="field-select" style={{ width: 'auto' }} value={companyFilter} onChange={e => { setCompanyFilter(e.target.value); setPage(1); }}>
          <option value="">Toutes les entreprises</option>
          <option value="pending">Entreprises à vérifier</option>
          <option value="verified">Entreprises validées</option>
        </select>
      </div>

      <div className="card" style={{ overflowX: 'auto' }}>
        {loading ? (
          <div className="skeleton" style={{ height: 200 }} />
        ) : (
          <table className="c2s-table">
            <thead>
              <tr>
                <th>Utilisateur</th><th>Email</th><th>Rôle</th><th>KYC</th><th>PRO</th><th>Inscription</th><th>Statut</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => {
                const pro = proState(u);
                const isCarrier = u.roles?.includes('ROLE_CARRIER');
                const isCompany = u.profile_type === 'PROFESSIONAL';
                const num = companyNumber(u);
                const url = num ? annuaireUrl(num) : null;
                const fr = frenchCompanyNumber(num);
                const overridden = u.pro_admin_override === true || u.pro_admin_override === false;
                return (
                  <tr key={u.id} data-testid={`user-row-${u.id}`}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div className="av sm av-teal">{(u.first_name || '?')[0]}</div>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{u.first_name} {u.last_name}</div>
                          {u.company_name && <div style={{ fontSize: 11, color: 'var(--ink3)' }}>{u.company_name}</div>}
                          {isCompany && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3, flexWrap: 'wrap' }} data-testid={`company-${u.id}`}>
                              <span style={{ fontSize: 11, color: 'var(--ink2)', fontVariantNumeric: 'tabular-nums' }}>{num ? `SIRET ${num}` : 'SIRET non renseigné'}</span>
                              <span style={chipStyle(u.company_verified ? 'teal' : 'grey')}>{u.company_verified ? 'Entreprise validée' : 'À vérifier'}</span>
                              {num && (
                                <span style={{ fontSize: 11, color: fr.kind ? (fr.valid ? 'var(--teal)' : 'var(--red)') : 'var(--ink3)' }} data-testid={`siret-format-${u.id}`}>
                                  {fr.kind ? (fr.valid ? `Format ${fr.kind} valide` : `${fr.kind} invalide (clé)`) : 'Numéro hors France'}
                                </span>
                              )}
                              {url && (
                                <a href={url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                                  Annuaire <ExternalLink size={10} aria-hidden="true" />
                                </a>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td style={{ fontSize: 12 }}>{u.email}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {u.roles?.map(r => <span key={r} className={`badge ${r === 'ROLE_ADMIN' ? 'badge-admin' : r === 'ROLE_CARRIER' ? 'badge-trajet' : 'badge-colis'}`} style={{ fontSize: 10 }}>{roleLabel(r)}</span>)}
                      </div>
                    </td>
                    <td><KycBadge status={u.kyc_status} /></td>
                    <td data-testid={`pro-${u.id}`}>
                      {pro ? <span style={chipStyle(pro.tone)}>{pro.label}</span> : <span style={{ color: 'var(--ink3)', fontSize: 12 }}>—</span>}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--ink3)' }}>{formatDate(u.created_at)}</td>
                    <td>
                      {u.is_suspended
                        ? <span style={{ color: 'var(--red)', fontSize: 12, fontWeight: 600 }}>Suspendu</span>
                        : u.is_verified
                          ? <span style={{ color: 'var(--teal)', fontSize: 12, fontWeight: 600 }}>Actif</span>
                          : <span style={{ color: 'var(--gold)', fontSize: 12, fontWeight: 600 }}>Non vérifié</span>}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {!u.is_verified && !u.roles?.includes('ROLE_ADMIN') && (
                          <button data-testid={`btn-verify-${u.id}`} className="btn btn-teal btn-sm" style={{ fontSize: 11, padding: '3px 8px' }} onClick={() => doAction(u.id, 'verify')}>
                            <Shield size={11} /> Vérifier
                          </button>
                        )}
                        {isCarrier && !u.pro_subscribed && (
                          <button data-testid={`btn-pro-offer-${u.id}`} className="btn btn-gold btn-sm" style={actionStyle} onClick={() => askPro(u, true)}>
                            <Crown size={11} aria-hidden="true" /> Offrir le PRO
                          </button>
                        )}
                        {isCarrier && u.pro_subscribed && (
                          <button data-testid={`btn-pro-remove-${u.id}`} className="btn btn-ghost btn-sm" style={actionStyle} onClick={() => askPro(u, false)}>
                            <Crown size={11} aria-hidden="true" /> Retirer le PRO
                          </button>
                        )}
                        {isCarrier && overridden && (
                          <button data-testid={`btn-pro-stripe-${u.id}`} className="btn btn-ghost btn-sm" style={actionStyle} onClick={() => askPro(u, null)} title="Le statut PRO suivra de nouveau l'abonnement Stripe">
                            <RotateCcw size={11} aria-hidden="true" /> Rendre à Stripe
                          </button>
                        )}
                        {isCompany && !u.company_verified && (
                          <button data-testid={`btn-company-verify-${u.id}`} className="btn btn-teal btn-sm" style={actionStyle} onClick={() => askCompany(u, true)}>
                            <BadgeCheck size={11} aria-hidden="true" /> Valider l'entreprise
                          </button>
                        )}
                        {isCompany && u.company_verified && (
                          <button data-testid={`btn-company-unverify-${u.id}`} className="btn btn-ghost btn-sm" style={actionStyle} onClick={() => askCompany(u, false)}>
                            <BadgeCheck size={11} aria-hidden="true" /> Retirer la validation
                          </button>
                        )}
                        {isCompany && (
                          <button data-testid={`btn-company-revert-${u.id}`} className="btn btn-ghost btn-sm" style={actionStyle} onClick={() => askRevert(u)}>
                            <Undo2 size={11} aria-hidden="true" /> Repasser en Particulier
                          </button>
                        )}
                        {!u.is_suspended && !u.roles?.includes('ROLE_ADMIN') && (
                          <button data-testid={`btn-suspend-${u.id}`} className="btn btn-danger btn-sm" style={{ fontSize: 11, padding: '3px 8px' }} onClick={() => setSuspendTarget(u)} title="Suspendre le compte">
                            <UserX size={11} />
                          </button>
                        )}
                        {u.is_suspended && (
                          <button data-testid={`btn-reactivate-${u.id}`} className="btn btn-ghost btn-sm" style={{ fontSize: 11, padding: '3px 8px' }} onClick={() => doAction(u.id, 'reactivate')} title="Réactiver le compte">
                            <UserCheck size={11} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {total > 20 && (
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 16 }}>
            <button className="btn btn-ghost btn-sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Préc.</button>
            <span style={{ fontSize: 13, alignSelf: 'center' }}>Page {page} / {Math.ceil(total / 20)}</span>
            <button className="btn btn-ghost btn-sm" disabled={page >= Math.ceil(total / 20)} onClick={() => setPage(p => p + 1)}>Suiv. →</button>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!suspendTarget}
        onClose={() => setSuspendTarget(null)}
        onConfirm={() => { const u = suspendTarget; setSuspendTarget(null); doAction(u.id, 'suspend'); }}
        variant="destructive"
        icon="ban"
        title="Suspendre ce compte ?"
        subtitle={suspendTarget ? `${suspendTarget.first_name} ${suspendTarget.last_name} — ${suspendTarget.email}` : undefined}
        warn="L'utilisateur ne pourra plus se connecter ni effectuer d'actions."
        confirmLabel="Confirmer la suspension"
      />

      <ConfirmDialog
        open={!!pending}
        onClose={() => { if (!busy) setPending(null); }}
        onConfirm={confirmPending}
        variant={pending?.variant}
        icon={pending?.icon}
        title={pending?.title}
        subtitle={pending?.subtitle || (pending ? `${name(pending.u)} — ${pending.u.email}` : undefined)}
        details={pending?.info}
        warn={pending?.warn}
        confirmLabel={pending?.confirmLabel}
        busy={busy}
        inputPrompt={pending?.kind === 'revert' ? { label: "Motif (envoyé à l'utilisateur)", placeholder: "Ex. : SIRET introuvable dans l'annuaire des entreprises" } : undefined}
        inputValue={rejectReason}
        onInputChange={setRejectReason}
      />
    </div>
  );
}
